
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.1.1
 * @copyright  Copyright (C) 2024, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *  2024-08-23 (1.0.0) (jhoward)
 *    - Refactor textual default item from <textual-menu> to <default-item>.
 *
 *  2024-08-23 (1.1.0) (jhoward)
 *    - Handle selected menu items via store in textual <default-item>.
 *    - Support pricing types in textual <default-item>.
 *    - Report item content rows in textual <default-item>.
 *    - Refactor textual menu item CSS to textual <default-item>.
 *
 *  2024-12-03 (1.1.1) (jhoward)
 *    - Add isCartEnabled prop to <default-textual-item>.
 */

import {defineComponent, PropType} from 'vue'
import MenuTag from "../../menu-tag.vue";
import {Menu, MenuItem} from "../../defs";
import {
  computedIsScheduled, methodGetPricingTypes,
  methodGetItemBIN,
  methodRemoveBINFromTitle,
  methodPricePerType
} from "../../../../helpers/menu-sections";

export default defineComponent({
  name: "defaultItem",
  emits: {
    'rows': (rows: number) => true
  },
  components: {MenuTag},
  props: {
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    item: {
      type: Object as PropType<MenuItem>,
      required: true
    },
    itemIndex: {
      type: Number,
      required: true
    },
    isCartEnabled: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.$emit('rows', this.itemRows);
  },
  data() {
    return {
      pricingTypes: this.getPricingTypes([this.item]).map(priceType => {
        return [
          priceType,
          this.getPricePerType(
            this.item.mi_price,
            priceType,
            this.item.mi_price
          )
        ];
      }) as [string, string][]
    }
  },
  watch: {
    itemRows() {
      this.$emit('rows', this.itemRows);
    },
  },
  computed: {
    isScheduled: computedIsScheduled,
    containerClasses(): { [key: string]: boolean } {
      const output: { [key: string]: boolean } = {
        'item-highlight': !!this.item.mi_highlight
      };
      output['item-' + this.itemIndex] = true;
      output['delay-' + (this.itemIndex % 2)] = true;

      return output;
    },
    isPricingTypesShown(): boolean {
      return !!this.pricingTypes.length;
    },
    isBodyShown(): boolean {
      return !!this.item.mi_subtitle.length || !!this.item.mi_description.length;
    },
    isTagsShown(): boolean {
      return !!this.item.tags.length;
    },
    itemRows(): number {
      let output = 1; //title
      if (this.isBodyShown) {
        output ++;
      }
      if (this.isTagsShown) {
        output ++;
      }
      if (this.isPricingTypesShown) {
        output ++;
      }

      return output;
    },
    bin(): string {
      return this.getItemBin(this.item);
    },
    isBinShown(): boolean {
      return !!this.bin.length;
    },
  },
  methods: {
    getItemBin: methodGetItemBIN,
    getTitleWithoutBin: methodRemoveBINFromTitle,
    getPricingTypes: methodGetPricingTypes,
    getPricePerType: methodPricePerType
  }
})
